import { forwardRef, ReactNode, useMemo, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Badge,
  BadgeProps,
  ButtonBase,
  ButtonBaseProps,
  Menu,
  MenuItem,
} from '@mui/material';
import { useAlerts } from 'contexts/alertsContext';
import { useAuth } from 'contexts/authContext';
import { Link, matchPath, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { getIcon } from 'utils/icons.utils';
import { NavMenu } from './navOptions';

export const createMenuTab = (
  navMenu: NavMenu,
  additionalMenuItems?: Array<() => ReactNode>,
) =>
  forwardRef<HTMLButtonElement, ButtonBaseProps>(function MenuTab(props, ref) {
    const { hasViewPermission } = useAuth();
    const { state } = useAlerts();
    const { pathname } = useLocation();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) =>
      setAnchorEl(event.currentTarget);

    const handleMenuClose = () => setAnchorEl(null);

    const visibleMenuItems = useMemo(
      () =>
        navMenu.menuItems.filter(({ requiredRoles }) =>
          hasViewPermission(requiredRoles),
        ),
      [hasViewPermission],
    );

    const isMenuEmpty = useMemo(
      () =>
        visibleMenuItems.length === 0 &&
        (!additionalMenuItems || additionalMenuItems.length === 0),
      [visibleMenuItems],
    );

    const isMenuOpen = Boolean(anchorEl);

    if (isMenuEmpty) return null;

    const { children, ...buttonProps } = props;

    return (
      <>
        <ButtonBase ref={ref} {...buttonProps} onClick={handleMenuOpen}>
          {children}
          <KeyboardArrowDownIcon
            sx={{
              ml: 1,
              transform: `rotate(${isMenuOpen ? '-180deg' : '0deg'})`,
              transition: 'transform 0.152s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          />
        </ButtonBase>
        <Menu
          anchorEl={anchorEl}
          id={navMenu.name}
          keepMounted
          open={isMenuOpen}
          onClose={handleMenuClose}
        >
          {visibleMenuItems.map(
            ({ title, url, value, name, icon, alertsContextKey }) => {
              const isSelected = !!matchPath(value, pathname);
              return (
                <MenuItem
                  key={name}
                  to={url}
                  component={Link}
                  selected={isSelected}
                  onClick={handleMenuClose}
                  sx={{
                    paddingRight:
                      alertsContextKey !== undefined ? '2.35rem' : '',
                  }}
                >
                  {icon && getIcon(icon, { sx: { marginRight: '.5rem' } })}
                  {alertsContextKey !== undefined ? (
                    <StyledBadge
                      badgeContent={state[alertsContextKey]}
                      showZero
                      color='error'
                    >
                      {title}
                    </StyledBadge>
                  ) : (
                    title
                  )}
                </MenuItem>
              );
            },
          )}
          {additionalMenuItems?.map((SubItem) => SubItem())}
        </Menu>
      </>
    );
  });

const StyledBadge = styled(Badge)<BadgeProps>(({ theme }) => ({
  '& .MuiBadge-badge': {
    right: -15,
    top: 12,
    padding: '0 4px',
  },
}));
